import React, { useState, useEffect, useRef } from 'react';
import { Send, ArrowLeft, Video, Flag, Image, Smile } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import UpgradeModal from './UpgradeModal';
import ReportUserDialog from './ReportUserDialog';

const Chat = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [ws, setWs] = useState(null);
  const pollingIntervalRef = useRef(null);
  const [isPolling, setIsPolling] = useState(true);
  const [userTier, setUserTier] = useState(null); // null = checking, 'free'/'premium'/'vip'
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const previousMessageCountRef = useRef(0);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    if (conversationId) {
      // FIRST: Check user subscription tier
      checkUserTier();
    }

    return () => {
      if (ws) {
        ws.close();
      }
      // Clean up polling interval
      stopPolling();
    };
  }, [conversationId]);

  // Load chat content only after tier check passes
  useEffect(() => {
    if (conversationId && userTier !== null && userTier !== 'free') {
      // Only load chat if user is premium or VIP
      loadConversation();
      loadMessages();
      connectWebSocket();
      
      // Start polling for new messages every 3 seconds
      startPolling();
    }
  }, [conversationId, userTier]);

  useEffect(() => {
    // Only auto-scroll if:
    // 1. New messages were added (count increased)
    // 2. User is near bottom (shouldAutoScroll is true)
    const newMessageAdded = messages.length > previousMessageCountRef.current;
    
    if (newMessageAdded && shouldAutoScroll) {
      console.log('📜 Auto-scrolling to new message');
      scrollToBottom();
    }
    
    previousMessageCountRef.current = messages.length;
  }, [messages, shouldAutoScroll]);
  
  // Detect if user has scrolled up manually
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    // If user is within 100px of bottom, enable auto-scroll
    // If user scrolled up more than 100px, disable auto-scroll
    if (distanceFromBottom < 100) {
      if (!shouldAutoScroll) {
        console.log('📜 User near bottom - enabling auto-scroll');
        setShouldAutoScroll(true);
      }
    } else {
      if (shouldAutoScroll) {
        console.log('📜 User scrolled up - disabling auto-scroll');
        setShouldAutoScroll(false);
      }
    }
  };

  // Pause/resume polling based on page visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('🔕 Page hidden - pausing message polling');
        stopPolling();
      } else {
        console.log('🔔 Page visible - resuming message polling');
        startPolling();
        // Immediately fetch messages when tab becomes visible
        loadMessages();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [conversationId]);

  const startPolling = () => {
    // Clear any existing interval first
    stopPolling();
    
    console.log('✅ Starting message polling (every 3 seconds)');
    pollingIntervalRef.current = setInterval(() => {
      console.log('🔄 Polling for new messages...');
      loadMessages();
    }, 3000); // Poll every 3 seconds
    setIsPolling(true);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      console.log('⏸️ Stopping message polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      setIsPolling(false);
    }
  };

  const checkUserTier = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const tier = data.subscription_tier || 'free';
        console.log('💎 User tier:', tier);
        setUserTier(tier);
        
        // If user is free tier, show upgrade modal
        if (tier === 'free') {
          console.log('🚫 Free user attempting to access chat - showing upgrade modal');
          setShowUpgradeModal(true);
          setLoading(false); // Stop loading spinner
        }
      } else {
        // If can't fetch tier, assume free to be safe
        setUserTier('free');
        setShowUpgradeModal(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking user tier:', error);
      // If error, assume free to be safe
      setUserTier('free');
      setShowUpgradeModal(true);
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    try {
      const token = localStorage.getItem('token');
      const userId = JSON.parse(atob(token.split('.')[1])).sub; // Extract user ID from JWT
      const wsUrl = `${process.env.REACT_APP_BACKEND_URL.replace('https', 'wss').replace('http', 'ws')}/ws/${userId}`;
      
      const websocket = new WebSocket(wsUrl);
      
      websocket.onopen = () => {
        console.log('WebSocket connected');
        setWs(websocket);
      };
      
      websocket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message' && data.conversation_id === conversationId) {
          // Add new message to the list
          const newMsg = {
            message_id: data.message_id,
            content: data.content,
            sent_at: data.sent_at,
            sender_name: data.sender_name,
            is_own_message: false
          };
          setMessages(prev => [...prev, newMsg]);
        }
      };
      
      websocket.onclose = () => {
        console.log('WebSocket disconnected');
        setWs(null);
      };
      
      websocket.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
    } catch (error) {
      console.error('WebSocket connection failed:', error);
    }
  };

  const loadConversation = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const conv = data.conversations.find(c => c.conversation_id === conversationId);
        setConversation(conv);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('📥 Loading messages for conversation:', conversationId);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📨 Loaded messages:', data.messages?.length || 0, 'messages');
        console.log('📨 Messages data:', data.messages);
        setMessages(data.messages || []);
      } else {
        console.error('❌ Failed to load messages:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !conversation) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          match_id: conversation.match_id,
          content: newMessage,
          message_type: 'text'
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('💬 Message sent, response:', data);
        
        // Add the message to the local state immediately
        setMessages(prev => [...prev, data]);
        setNewMessage('');
        
        // Enable auto-scroll when user sends a message
        setShouldAutoScroll(true);
        
        console.log('✅ Message added to local state');
      } else {
        console.error('❌ Failed to send message:', response.status, response.statusText);
        const errorData = await response.json().catch(() => ({}));
        console.error('Error details:', errorData);
      }
    } catch (error) {
      console.error('❌ Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const startVideoCall = () => {
    console.log('🎥 Starting video call...');
    console.log('📊 Conversation:', conversation);
    
    if (!conversation) {
      alert('Conversation not loaded yet. Please wait a moment and try again.');
      return;
    }
    
    if (!conversation.match_id) {
      alert('Match information not available. Please try again.');
      return;
    }
    
    console.log('✅ Navigating to video call with partner:', conversation.participant.name);
    
    // Navigate to video call with partner info
    navigate(`/video-call`, {
      state: { 
        partnerInfo: {
          id: conversation.participant.id,
          name: conversation.participant.name,
          photo_url: conversation.participant.photo_url,
          matchId: conversation.match_id
        }
      }
    });
  };

  // Show loading while checking tier or loading chat
  if (loading || userTier === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-center justify-center overflow-x-hidden">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  // If user is free tier, show upgrade modal
  if (userTier === 'free') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-center justify-center overflow-x-hidden">
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => {
            setShowUpgradeModal(false);
            navigate('/connections'); // Redirect to connections page
          }}
          feature="chat messaging"
          message="Upgrade to Premium or VIP to unlock unlimited messaging with your matches!"
        />
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-center justify-center overflow-x-hidden">
        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Conversation Not Found</h2>
          <button
            onClick={() => navigate('/matches')}
            className="bg-white text-rose-500 px-6 py-3 rounded-full font-semibold hover:bg-rose-50 transition-colors"
          >
            Back to Matches
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-md border-b border-white/20 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/matches')}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <ArrowLeft className="h-6 w-6 text-white" />
            </button>
            
            <div className="flex items-center space-x-3">
              {conversation.participant.photo_url ? (
                <img
                  src={conversation.participant.photo_url}
                  alt={conversation.participant.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-white/20"
                />
              ) : (
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {conversation.participant.name.charAt(0)}
                  </span>
                </div>
              )}
              <div>
                <h2 className="text-xl font-semibold text-white">{conversation.participant.name}</h2>
                <p className="text-sm text-white/80">Online</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={startVideoCall}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
              title="Start Video Call"
            >
              <Video className="h-6 w-6 text-white" />
            </button>
            <button
              onClick={() => setShowReport(true)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
              title="Report"
              aria-label="Report"
              data-testid="chat-report-btn"
            >
              <Flag className="h-6 w-6 text-white" />
            </button>
          </div>
        </div>
      </div>
      <ReportUserDialog
        open={showReport}
        user={conversation.participant}
        onClose={() => setShowReport(false)}
        onReported={() => navigate('/chat')}
      />

      {/* Messages */}
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-4" style={{ height: 'calc(100vh - 200px)' }}>
          <div 
            ref={messagesContainerRef}
            onScroll={handleScroll}
            className="h-full overflow-y-auto space-y-4 pr-2"
          >
            {console.log('🎨 Rendering', messages.length, 'messages')}
            {messages.length === 0 && (
              <div className="text-center text-white/60 py-8">
                No messages yet. Start the conversation!
              </div>
            )}
            {messages.map((message) => (
              <div
                key={message.message_id}
                className={`flex ${message.is_own_message ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                    message.is_own_message
                      ? 'bg-rose-500 text-white rounded-br-md'
                      : 'bg-white/20 text-white rounded-bl-md'
                  }`}
                >
                  <p className="text-sm">{message.content}</p>
                  <p className={`text-xs mt-1 ${message.is_own_message ? 'text-rose-100' : 'text-white/60'}`}>
                    {new Date(message.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Message Input */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4">
          <form onSubmit={sendMessage} className="flex items-center space-x-3">
            <button
              type="button"
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <Image className="h-5 w-5 text-white/60" />
            </button>
            <button
              type="button"
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <Smile className="h-5 w-5 text-white/60" />
            </button>
            
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-white placeholder-white/60 focus:outline-none focus:border-white/40"
              disabled={sending}
            />
            
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className="p-2 bg-rose-500 hover:bg-rose-600 disabled:bg-rose-300 rounded-full transition-colors"
            >
              <Send className="h-5 w-5 text-white" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Chat;